<script lang="ts" setup>
import { useChatConversation } from '~/composables/useChatConversation';
import { useChatTicketCard } from '~/composables/useChatTicketCard';
import { supportTicketService } from '~/services/support-ticket.service';
import {
  createAgentChatClient,
  createChatTokenProvider,
  fetchChatToken,
  type AgentChatClient,
} from '~/services/agent-chat.service';
import { useAuthStore } from '~/store/auth';
import { useChatPanelStore } from '~/store/chatPanel';
import { usePageGuideStore } from '~/store/pageGuide';
import {
  CHAT_MESSAGE_MAX_LENGTH,
  chatContext,
  chatErrorKey,
  chatProgressKey,
  chatSuggestionKeys,
  activeRunKey,
  clearActiveRun,
  isTurnEnded,
  readActiveRun,
  writeActiveRun,
  type ChatTurn,
} from '~/utils/agent-chat';
import { historyItems, historyNextPage, type ChatHistoryItem } from '~/utils/agent-chat-history';
import { isSystemManager } from '~/utils/account';
import { pageSnapshot } from '~/utils/page-snapshot';
import { formatDateTime } from '~/utils/datetime';
import type { ChatHistoryCursor, ChatRatingReason, ChatSessionSummary } from '~/services/agent-chat.service';

const { t, locale } = useI18n();
const config = useRuntimeConfig();
const panel = useChatPanelStore();
const { operator, currentShop } = storeToRefs(useAuthStore());

// ── Client ───────────────────────────────────────────────────────────────────
const chatLocale = () => (locale.value === 'en' ? 'en' : 'ja');
const tokens = createChatTokenProvider(() => fetchChatToken(chatLocale()));
// The token holds the locale, so a new locale needs a new token.
watch(locale, () => tokens.invalidate());
let client: AgentChatClient | null = null;
const getClient = () => (client ??= createAgentChatClient({ baseUrl: String(config.public.agentApiUrl), tokens }));

// ── Context chip ─────────────────────────────────────────────────────────────
const chipRemoved = ref(false);
// A new page gives a new chip.
watch(
  () => panel.page?.path,
  () => (chipRemoved.value = false),
);
const context = computed(() => chatContext(panel.page, chipRemoved.value));
// The snapshot is from the moment that the user sends the question.
const runContext = () => (context.value ? chatContext(panel.page, false, pageSnapshot(document)) : undefined);

// ── Active run ───────────────────────────────────────────────────────────────
const sessionStore = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};
const runKey = () => (operator.value?.uid ? activeRunKey(operator.value.uid, currentShop.value?.uid) : null);
// The key of the last write, so that the clear removes that entry.
let writtenKey: string | null = null;
const activeRun = {
  read: () => {
    const key = runKey();
    return key ? readActiveRun(sessionStore(), key) : null;
  },
  write: (run: Parameters<typeof writeActiveRun>[2]) => {
    writtenKey = runKey();
    if (writtenKey) writeActiveRun(sessionStore(), writtenKey, run);
  },
  clear: () => {
    const key = writtenKey ?? runKey();
    if (key) clearActiveRun(sessionStore(), key);
    writtenKey = null;
  },
};

const chat = useChatConversation({
  client: getClient,
  context: runContext,
  onAnswer: () => panel.answerArrived(),
  activeRun,
});
const { turns, busy } = chat;
// The run continues in agent-api. Only the reading of its events stops.
onBeforeUnmount(() => chat.dispose());
// A page reload during an answer resumes it. The ticket card of the session shows as from the history.
onMounted(() => void chat.restore(() => ticket.showTicket(chat.ticketUid.value)));

// ── Composer ─────────────────────────────────────────────────────────────────
const draft = ref('');
const send = async (text = draft.value) => {
  if (busy.value || text.trim() === '') return;
  if (text === draft.value) draft.value = '';
  await chat.send(text);
};
const onKeydown = (event: KeyboardEvent) => {
  // Enter sends. Shift+Enter makes a new line, and Enter during an IME composition confirms the text.
  if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
  event.preventDefault();
  send();
};

const suggestions = computed(() => chatSuggestionKeys(panel.page?.path ?? '/').map(key => t(key)));

// ── Answers ──────────────────────────────────────────────────────────────────
const pageGuide = usePageGuideStore();
const errorText = (turn: ChatTurn) =>
  t(chatErrorKey({ status: turn.error?.status ?? 0, code: turn.error?.code ?? 'unknown' }));
const canRetry = (turn: ChatTurn) =>
  !busy.value && isTurnEnded(turn) && (turn.status === 'failed' || turn.status === 'cancelled');

// ── Menu and history ─────────────────────────────────────────────────────────
const view = ref<'chat' | 'history'>('chat');
const historySessions = ref<ChatSessionSummary[]>([]);
const historyCursor = ref<ChatHistoryCursor | null>(null);
const historyLoading = ref(false);
const historyFailed = ref(false);
const history = computed<ChatHistoryItem[]>(() => historyItems(historySessions.value));

const loadHistory = async (more = false) => {
  if (historyLoading.value) return;
  historyLoading.value = true;
  historyFailed.value = false;
  try {
    const page = await getClient().listSessions(more ? (historyCursor.value ?? undefined) : undefined);
    historySessions.value = more ? [...historySessions.value, ...page] : page;
    historyCursor.value = historyNextPage(page);
  } catch {
    historyFailed.value = true;
  } finally {
    historyLoading.value = false;
  }
};

const showHistory = () => {
  view.value = 'history';
  loadHistory();
};

const newChat = () => {
  chat.reset();
  ticket.showTicket(null);
  view.value = 'chat';
};

const opening = ref(false);
const openFailed = ref(false);
const openSession = async (id: string) => {
  if (opening.value) return;
  opening.value = true;
  openFailed.value = false;
  try {
    // The session shows its ticket, or no card.
    if (await chat.open(id)) ticket.showTicket(chat.ticketUid.value);
    view.value = 'chat';
  } catch {
    openFailed.value = true;
  } finally {
    opening.value = false;
  }
};

// ── Ticket card ──────────────────────────────────────────────────────────────
const ticket = useChatTicketCard({
  conversation: chat,
  isSystemManager: () => isSystemManager(operator.value),
  shopUid: () => currentShop.value?.uid,
  createTicket: (shopUid, title, body) => supportTicketService.create(shopUid, title, body),
  recordTicket: (sessionId, ticketUid) => getClient().recordTicket(sessionId, ticketUid),
});
const ticketCard = ticket.card;
const ticketAllowed = ticket.allowed;

// ── Ratings ──────────────────────────────────────────────────────────────────
const REASONS: ChatRatingReason[] = ['wrong', 'unclear', 'not_what_i_asked'];

const ratingFailed = ref<string | null>(null);
const rate = async (turn: ChatTurn, value: 'up' | 'down', reason?: ChatRatingReason) => {
  ratingFailed.value = null;
  try {
    await chat.rate(turn.id, value, reason);
  } catch {
    ratingFailed.value = turn.id;
  }
};

// Keep the newest text in view while it streams.
const feed = ref<HTMLElement | null>(null);
watch(
  () => [turns.value.length, turns.value.at(-1)?.text.length, turns.value.at(-1)?.status],
  async () => {
    await nextTick();
    feed.value?.scrollTo({ top: feed.value.scrollHeight });
  },
);
</script>

<template>
  <!-- A lower order than the app bar gives the panel the full window height, like the sidebar. -->
  <right-dock-panel
    data-suge-ignore
    :order="-1"
    class="chat-panel"
    :model-value="panel.open"
    storage-key="chat-panel-width"
    :back-label="t('chat.back')"
    :resize-label="t('chat.resize')"
    @update:model-value="value => (value ? panel.show() : panel.hide())"
  >
    <template #header>
      <v-icon
        color="primary"
        size="20"
      >
        mdi-creation
      </v-icon>
      <span class="chat-panel__title">{{ t('chat.title') }}</span>
      <v-menu location="bottom end">
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            icon="mdi-dots-vertical"
            variant="text"
            size="small"
            :aria-label="t('chat.menu')"
          />
        </template>
        <v-list density="compact">
          <v-list-item
            prepend-icon="mdi-plus"
            :title="t('chat.new_chat')"
            @click="newChat"
          />
          <v-list-item
            prepend-icon="mdi-history"
            :title="t('chat.history')"
            @click="showHistory"
          />
          <v-list-item
            v-if="ticketAllowed"
            prepend-icon="mdi-ticket-outline"
            :title="t('chat.ticket.menu')"
            :disabled="busy"
            @click="
              view = 'chat';
              ticket.openCard();
            "
          />
        </v-list>
      </v-menu>
      <v-btn
        icon="mdi-close"
        variant="text"
        size="small"
        :aria-label="t('buttons.close')"
        @click="panel.hide()"
      />
    </template>

    <div
      v-if="view === 'history'"
      class="chat-history"
    >
      <div class="chat-history__bar">
        <v-btn
          icon="mdi-arrow-left"
          variant="text"
          size="small"
          :aria-label="t('chat.back')"
          @click="view = 'chat'"
        />
        <span class="chat-history__title">{{ t('chat.history') }}</span>
      </div>
      <v-list
        lines="two"
        density="compact"
      >
        <v-list-item
          v-for="item in history"
          :key="item.id"
          :active="item.id === chat.sessionId.value"
          :disabled="opening"
          @click="openSession(item.id)"
        >
          <v-list-item-title>{{ item.title ?? t('chat.new_chat') }}</v-list-item-title>
          <v-list-item-subtitle>{{ formatDateTime(item.date, undefined, 'short') }}</v-list-item-subtitle>
          <template
            v-if="item.hasTicket"
            #append
          >
            <v-chip
              size="x-small"
              color="primary"
              variant="tonal"
              label
              prepend-icon="mdi-ticket-outline"
            >
              {{ t('chat.ticket_badge') }}
            </v-chip>
          </template>
        </v-list-item>
      </v-list>
      <p
        v-if="!historyLoading && !historyFailed && history.length === 0"
        class="chat-history__note"
      >
        {{ t('chat.history_empty') }}
      </p>
      <p
        v-if="historyFailed"
        class="chat-history__note chat-history__note--error"
      >
        {{ t('chat.history_failed') }}
      </p>
      <p
        v-if="openFailed"
        class="chat-history__note chat-history__note--error"
      >
        {{ t('chat.open_failed') }}
      </p>
      <div class="chat-history__more">
        <v-progress-circular
          v-if="historyLoading"
          indeterminate
          size="20"
          width="2"
        />
        <m-btn
          v-else-if="historyCursor"
          size="small"
          variant="text"
          @click="loadHistory(true)"
        >
          {{ t('chat.history_more') }}
        </m-btn>
      </div>
    </div>

    <div
      v-else
      ref="feed"
      class="chat-feed"
    >
      <div
        v-if="turns.length === 0"
        class="chat-empty"
      >
        <div class="chat-avatar chat-avatar--large">
          <v-icon size="28">mdi-creation</v-icon>
        </div>
        <p class="chat-empty__greeting">
          {{ operator?.name ? t('chat.greeting', { name: operator.name }) : t('chat.greeting_anonymous') }}
        </p>
        <p class="chat-empty__hint">{{ t('chat.greeting_hint') }}</p>
        <div class="chat-empty__chips">
          <v-chip
            v-for="suggestion in suggestions"
            :key="suggestion"
            variant="outlined"
            size="small"
            @click="send(suggestion)"
          >
            {{ suggestion }}
          </v-chip>
        </div>
      </div>

      <template
        v-for="turn in turns"
        :key="turn.id"
      >
        <div
          v-if="turn.question"
          class="chat-question"
        >
          <div class="chat-bubble chat-bubble--mine chat-question__text">{{ turn.question }}</div>
        </div>

        <div class="chat-answer">
          <div
            v-if="turn.text"
            class="chat-bubble chat-bubble--other"
          >
            <chat-answer-body
              :turn-id="turn.id"
              :text="turn.text"
              class="chat-bubble-preview"
            />
          </div>

          <m-btn
            v-if="turn.status === 'completed' && turn.guide"
            size="small"
            variant="outlined"
            prepend-icon="mdi-cursor-default-click-outline"
            class="mt-2"
            @click="pageGuide.start(turn.guide)"
          >
            {{ t('chat.guide.start') }}
          </m-btn>

          <div
            v-if="chatProgressKey(turn)"
            class="chat-answer__progress"
          >
            <v-progress-circular
              indeterminate
              size="14"
              width="2"
            />
            {{ t(chatProgressKey(turn)!) }}
          </div>

          <div
            v-if="turn.status === 'refused'"
            class="chat-answer__note"
          >
            {{ t('chat.refused') }}
          </div>
          <div
            v-else-if="turn.status === 'failed'"
            class="chat-answer__note chat-answer__note--error"
          >
            {{ errorText(turn) }}
          </div>
          <div
            v-else-if="turn.status === 'cancelled'"
            class="chat-answer__note"
          >
            {{ t('chat.cancelled') }}
          </div>
          <m-btn
            v-if="canRetry(turn)"
            size="small"
            variant="outlined"
            prepend-icon="mdi-refresh"
            class="mt-2"
            @click="chat.retry(turn.id)"
          >
            {{ t('chat.retry') }}
          </m-btn>

          <div
            v-if="turn.status === 'completed' && turn.messageId"
            class="chat-rating"
          >
            <v-btn
              :icon="turn.rating?.value === 'up' ? 'mdi-thumb-up' : 'mdi-thumb-up-outline'"
              variant="text"
              size="x-small"
              :aria-label="t('chat.rate_up')"
              :aria-pressed="turn.rating?.value === 'up'"
              @click="rate(turn, 'up')"
            />
            <v-btn
              :icon="turn.rating?.value === 'down' ? 'mdi-thumb-down' : 'mdi-thumb-down-outline'"
              variant="text"
              size="x-small"
              :aria-label="t('chat.rate_down')"
              :aria-pressed="turn.rating?.value === 'down'"
              @click="rate(turn, 'down')"
            />
            <span
              v-if="ratingFailed === turn.id"
              class="chat-rating__error"
            >
              {{ t('chat.rate_failed') }}
            </span>
          </div>
          <div
            v-if="turn.rating?.value === 'down'"
            class="chat-rating__reasons"
          >
            <span>{{ t('chat.rate_reason_label') }}</span>
            <v-chip
              v-for="reason in REASONS"
              :key="reason"
              size="x-small"
              :variant="turn.rating.reason === reason ? 'flat' : 'outlined'"
              :color="turn.rating.reason === reason ? 'primary' : undefined"
              @click="rate(turn, 'down', reason)"
            >
              {{ t(`chat.rate_reasons.${reason}`) }}
            </v-chip>
          </div>
          <m-btn
            v-if="turn.rating?.value === 'down' && ticketAllowed && !ticketCard"
            size="small"
            variant="outlined"
            prepend-icon="mdi-ticket-outline"
            :disabled="busy"
            @click="ticket.openCard()"
          >
            {{ t('chat.ticket.menu') }}
          </m-btn>
        </div>
      </template>

      <chat-ticket-card
        v-if="ticketCard && ticketAllowed"
        :card="ticketCard"
        :has-shop="Boolean(currentShop?.uid)"
        @create="ticket.create()"
        @close="ticket.close()"
      />
    </div>

    <template #footer>
      <div
        v-if="view === 'chat'"
        class="chat-composer"
      >
        <div class="chat-composer__card">
          <v-chip
            v-if="context"
            size="small"
            variant="tonal"
            closable
            prepend-icon="mdi-file-document-outline"
            class="chat-composer__chip"
            :close-label="t('chat.context_remove')"
            @click:close="chipRemoved = true"
          >
            {{ t('chat.context_chip', { title: context.page_title }) }}
          </v-chip>
          <div class="chat-composer__row">
            <v-textarea
              v-model="draft"
              :placeholder="t('chat.input_placeholder')"
              :maxlength="CHAT_MESSAGE_MAX_LENGTH"
              rows="1"
              max-rows="6"
              auto-grow
              hide-details
              variant="plain"
              density="compact"
              class="chat-composer__input"
              @keydown="onKeydown"
            />
            <v-btn
              v-if="busy"
              icon="mdi-stop"
              color="primary"
              variant="tonal"
              size="small"
              :aria-label="t('chat.stop')"
              @click="chat.stop()"
            />
            <v-btn
              v-else
              icon="mdi-send"
              color="primary"
              size="small"
              :disabled="draft.trim() === ''"
              :aria-label="t('chat.send')"
              @click="send()"
            />
          </div>
        </div>
        <p class="chat-composer__note">{{ t('chat.input_note') }}</p>
      </div>
    </template>
  </right-dock-panel>
</template>

<style lang="scss" scoped>
.chat-panel__title {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-ds-primary);
}

.chat-feed {
  height: 100%;
  overflow-y: auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chat-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: rgb(var(--v-theme-primary));
  flex-shrink: 0;

  &--large {
    width: 48px;
    height: 48px;
  }
}

.chat-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  margin: auto 0;
  padding: 16px 8px;

  &__greeting {
    margin: 4px 0 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--color-ds-primary);
  }

  &__hint {
    margin: 0;
    font-size: 13px;
    color: var(--color-ds-secondary);
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    margin-top: 8px;
  }
}

.chat-question {
  display: flex;
  justify-content: flex-end;

  &__text {
    max-width: 85%;
    white-space: pre-wrap;
    font-size: 14px;
    line-height: 1.6;
  }
}

.chat-answer {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  &__progress {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--color-ds-secondary);
  }

  &__note {
    font-size: 13px;
    color: var(--color-ds-secondary);

    &--error {
      color: rgb(var(--v-theme-error));
    }
  }
}

.chat-history {
  height: 100%;
  overflow-y: auto;

  &__bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 8px 0;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
  }

  &__note {
    margin: 16px;
    font-size: 13px;
    color: var(--color-ds-secondary);
    text-align: center;

    &--error {
      color: rgb(var(--v-theme-error));
    }
  }

  &__more {
    display: flex;
    justify-content: center;
    padding: 8px;
  }
}

.chat-rating {
  display: flex;
  align-items: center;
  gap: 2px;

  &__error {
    font-size: 12px;
    color: rgb(var(--v-theme-error));
  }

  &__reasons {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--color-ds-secondary);
  }
}

.chat-composer {
  padding: 8px 12px 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  // A white card on the panel gradient keeps the input easy to read.
  &__card {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 10px 8px 14px;
    background: white;
    border: 1px solid #e3e8ee;
    border-radius: var(--radius-card);
    box-shadow: 0 2px 12px rgba(30, 50, 80, 0.1);

    &:focus-within {
      border-color: rgb(var(--v-theme-primary), 0.5);
    }
  }

  // The card is the field, thus the textarea has no background of its own.
  &__input :deep(.v-field) {
    background: transparent !important;
  }

  &__input :deep(.v-field__overlay) {
    display: none;
  }

  &__input :deep(.v-field__input) {
    padding-inline: 0;
  }

  &__chip {
    align-self: flex-start;
    max-width: 100%;
  }

  &__row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
  }

  // The note sits on the gray fill. This color keeps a contrast ratio above 4.5:1 there.
  &__note {
    margin: 0;
    font-size: 12px;
    color: #55534f;
    text-align: center;
  }
}
</style>

<style lang="scss">
// Vuetify renders the drawer with the scope of RightDockPanel, thus these rules are not scoped.
// The drawer rules of RightDockPanel use !important, thus the overrides here use it too.
// A white to cool gray fill and a soft shadow separate the chat from the warm page canvas.
.chat-panel.right-dock-panel {
  background: linear-gradient(180deg, #ffffff 0%, #eef1f5 35%, #dfe5ec 100%) !important;
  border-left: 1px solid #d5dbe3 !important;
  box-shadow: -6px 0 18px rgba(30, 50, 80, 0.08) !important;

  // The app bar is 64px high plus its 1px bottom border. The header box includes its border, so
  // 65px puts the two bottom borders on one line.
  .right-dock-panel__header {
    min-height: 65px;
  }

  // The composer card sits on the fill, so the footer has no background and no line.
  .right-dock-panel__footer {
    border-top: none;
  }

  .chat-bubble--other {
    background: white;
    box-shadow: 0 1px 3px rgba(30, 50, 80, 0.08);
  }
}
</style>
